import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {useAccountIdsToPublishTo, useWizardStep} from '@Components/social-media/social-media.hooks';
import {
  getOnboardingType,
  getPopupLoadingFunctionNameByOnboardingType,
  SocialMediaWizardOnboardingTypes,
  WizardStepIndex,
} from '@Components/social-media-wizard/components/social-media-wizard-onboarding/social-media-wizard-onboarding.types';
import type {SocialMediaAccountId} from '@Components/social-media/post.vo';
import {PUBLISH_PARAM_HANDLER_CLASS} from '@Components/social-media-wizard/social-media-wizard.types';

export function SocialMediaWizardOnboarding(): ReactElement {
  const hasSeenOnboarding = true;
  const wizardStep = useWizardStep();
  const [panelSkip, setPanelSkip] = useState([false, false, false, false, false, false]);
  const [, setCurrentStep] = useState(0);
  const accountIdsToPublishto = useAccountIdsToPublishTo();
  const pmwAppRef = useRef<HTMLDivElement>(null);

  const bodyjQuery = $('body');

  useEffect(() => {
    if (panelSkip[WizardStepIndex[wizardStep]] || hasSeenOnboarding) {
      $('.onboarding-container').remove();
      $('.onboarding-overlay').remove();
      return;
    }

    setCurrentStep(0);
    window.PMW.getJsAndCssAssetGroup('postermakerOnboardingPopups', () => {
      initWizardOnboardingPopups();
    });
  }, [wizardStep]);

  const shouldShowResizePopup = (): boolean => {
    return false;
  };

  useEffect(() => {
    const pmwAppDiv = $('#pmw-app')[0];
    const firstChild = pmwAppDiv.firstChild as HTMLElement;

    if (!firstChild) {
      return;
    }

    const appHeight = firstChild.clientHeight || 0;
    const appWidth = firstChild.clientWidth || 0;
    const navHeight = 56;

    if (pmwAppRef && pmwAppRef.current) {
      pmwAppRef.current.style.height = `${appHeight + navHeight}px`;
    }
  }, [pmwAppRef]);

  const addOverlay = (): void => {
    const pmwAppDiv = $('#pmw-app')[0];
    const firstChild = pmwAppDiv.firstChild as HTMLElement;

    if (!firstChild) {
      return;
    }

    const appHeight = firstChild.clientHeight || 0;
    const appWidth = firstChild.clientWidth || 0;
    const navHeight = 56;
    const container = $('<div ref={pmwAppRef} className="onboarding-overlay"></div>');

    container.css({
      height: `${appHeight + navHeight}px`,
      width: `${appWidth}px`,
      position: 'absolute',
      top: `${-navHeight}px`,
      'z-index': `${2000}`,
    });

    $('#pmw-app').append(container);
  };

  function initWizardOnboardingPopups(): void {
    const container = insertOnboardingPopupsContainer();
    const onboardingType = getOnboardingType(wizardStep);
    const showResizeOnboarding = shouldShowResizePopup();
    addOverlay();

    window.PMW.load(container, window.PMW.util.site_url(`socialmedia/${getPopupLoadingFunctionNameByOnboardingType(onboardingType, showResizeOnboarding)}`), {}, (): void => {
      revealFirstPopup();
      enableTransition();

      container.on('click', '.js-next-button', () => {
        handleNextPopup();
      });

      container.on('click', '.js-prev-button', () => {
        handlePrevPopup();
      });

      container.on('click', '.js-skip-walkthrough', () => {
        handleSkipOrSuccessfulOnboarding();
      });

      container.on('click', '.js-success-walkthrough', () => {
        handleSkipOrSuccessfulOnboarding();
      });
    });
  }

  const handleSkipOrSuccessfulOnboarding = (): void => {
    const updatedPanelSkip = [...panelSkip];
    const skippedindex = WizardStepIndex[wizardStep];
    updatedPanelSkip[skippedindex] = true;
    $('.onboarding-container').remove();
    $('.onboarding-overlay').remove();
    setPanelSkip(updatedPanelSkip);
  };

  const insertOnboardingPopupsContainer = (): JQuery<HTMLDivElement> => {
    const container = $(document.createElement('div')).addClass('onboarding-container');
    bodyjQuery.append(container);
    return container;
  };

  const hidePrevButton = (): void => {
    const prevButton = $('.js-prev-walkthrough');
    prevButton.addClass('hidden-visibility');
  };

  const disableTransitionForFirstPopup = (): void => {
    $('.user-walkthrough-popup:visible').addClass('disable-transition-for-first-popup');
    positionParentContainer(1);
  };

  const enableTransition = (): void => {
    $('.user-walkthrough-popup:visible').removeClass('disable-transition-for-first-popup');
  };

  const showPrevButton = (): void => {
    const prevButton = $('.js-prev-walkthrough');
    prevButton.removeClass('hidden-visibility');
  };

  const getCurrentlyOpenedPopup = (): JQuery<HTMLElement> => {
    return $(`.user-walkthrough-popup`).find('.walkthrough-container:visible').not('._hidden');
  };

  const setPopupClickability = (): void => {
    $(`.user-walkthrough-popup`).css({
      'z-index': `${3000}`,
    });
  };

  const revealFirstPopup = (): void => {
    setPopupClickability();
    hidePrevButton();
    disableTransitionForFirstPopup();
  };

  const handlePrevPopup = () => {
    const currentOpenedPopup = getCurrentlyOpenedPopup();
    const popUpToOpen = currentOpenedPopup.prev();
    changePopup(currentOpenedPopup, popUpToOpen);
  };

  const handleNextPopup = () => {
    const currentOpenedPopup = getCurrentlyOpenedPopup();
    const popUpToOpen = currentOpenedPopup.next();
    changePopup(currentOpenedPopup, popUpToOpen);
  };

  const getCurrentStepNumberForPopup = (popupToOpen: JQuery<HTMLElement>): number => {
    return popupToOpen.data('stepNumber') as number;
  };

  const getHeaderTitleToSetForPopupToOpen = (popupToOpen: JQuery<HTMLElement>): string => {
    return popupToOpen.data('headerTitle') as string;
  };

  const getTotalNumberOfSteps = (): number => {
    return $('.user-walkthrough-popup:visible').data('totalSteps') as number;
  };

  const setNewHeaderTitleForPopup = (popupToOpen: JQuery<HTMLElement>): void => {
    popupToOpen.siblings('.header-walkthrough:visible').children('.js-header-title-walkthrough').html(getHeaderTitleToSetForPopupToOpen(popupToOpen));
  };

  const setNewStepNumberForPopup = (popupToOpen: JQuery<HTMLElement>): void => {
    popupToOpen
      .siblings('.footer-steps:visible')
      .children('.js-step-number-walkthrough')
      .html(`${getCurrentStepNumberForPopup(popupToOpen)} / ${getTotalNumberOfSteps()}`);
  };

  const showNextAndSkipButtonsAndHideDoneButton = (): void => {
    const stepNumber = $('.js-step-number-walkthrough');
    const successButton = $('.js-success-walkthrough');
    const nextButton = $('.js-next-walkthrough');
    const skipLink = $('.js-skip-walkthrough');

    successButton.addClass('_hidden');
    nextButton.removeClass('_hidden');
    skipLink.removeClass('_hidden');
    stepNumber.addClass('content-primary');
    stepNumber.removeClass('color-success');
  };

  const hideNextAndSkipButtonsAndShowDoneButton = (): void => {
    const stepNumber = $('.js-step-number-walkthrough');
    const successButton = $('.js-success-walkthrough');
    const nextButton = $('.js-next-walkthrough');
    const skipLink = $('.js-skip-walkthrough');

    successButton.removeClass('_hidden');
    nextButton.addClass('_hidden');
    skipLink.addClass('_hidden');
    stepNumber.removeClass('content-primary');
    stepNumber.addClass('color-success');
  };

  const isFirstStep = (popupToOpen: JQuery<HTMLElement>): boolean => {
    return getCurrentStepNumberForPopup(popupToOpen) === 1;
  };

  const isLastStep = (popupToOpen: JQuery<HTMLElement>): boolean => {
    return getCurrentStepNumberForPopup(popupToOpen) === getTotalNumberOfSteps();
  };

  const closeCurrentPopupAndOpenNewPopup = (currentOpenedPopup: JQuery<HTMLElement>, popupToOpen: JQuery<HTMLElement>): void => {
    currentOpenedPopup.addClass('_hidden');
    popupToOpen.removeClass('_hidden');

    if (isFirstStep(popupToOpen)) {
      hidePrevButton();
    } else {
      showPrevButton();
    }

    if (isLastStep(popupToOpen)) {
      hideNextAndSkipButtonsAndShowDoneButton();
    } else {
      showNextAndSkipButtonsAndHideDoneButton();
    }
  };

  const getDesignSelectorOnboarding = (): {} => {
    const designSelectorOnboardingContainerWidth = document.getElementById('design-selector-onboarding-container')?.clientWidth;
    const popupWidth = $('.user-walkthrough-popup:visible').width();
    const tabsContainer = document.getElementById('design-selector-onboarding-container')?.querySelector('div');

    if (popupWidth && designSelectorOnboardingContainerWidth && tabsContainer) {
      const rightOffset = (window.innerWidth - (designSelectorOnboardingContainerWidth + 40 + 40)) / 2;
      const tabsWidth = tabsContainer.clientWidth;
      return {
        top: `180px`,
        left: `${(designSelectorOnboardingContainerWidth + 80 - popupWidth) / 2 + rightOffset}px`,
      };
    }

    return {};
  };

  const getSelectAccountsOnboarding = (stepNumber: number): {} => {
    if (stepNumber === 1) {
      return {
        top: `68px`,
        left: `20px`,
      };
    }

    if (stepNumber === 2) {
      const selectAccountsList = document.getElementById('select-accounts-onboarding-container');
      if (selectAccountsList) {
        const {offsetTop} = selectAccountsList;
        const {offsetLeft} = selectAccountsList;
        const popupHeight = $('.user-walkthrough-popup:visible').height();
        const containerHeight = selectAccountsList.clientHeight;

        return {
          top: `${offsetTop + (containerHeight - (popupHeight || 0)) / 2}px`,
          left: `${offsetLeft + 280}px`,
        };
      }
    }

    const popupWidth = $('.user-walkthrough-popup:visible').width();

    return {
      top: `68px`,
      left: `${window.innerWidth - 20 - (popupWidth || 0)}px`,
    };
  };

  const getExistingAccountsOnboarding = (): {} => {
    const connectAccountsGrid = document.getElementById('connect-accounts-onboarding-container');

    if (connectAccountsGrid) {
      const {offsetTop} = connectAccountsGrid;
      const gridWidth = connectAccountsGrid.clientWidth;
      const {offsetLeft} = connectAccountsGrid;
      const gridHeight = connectAccountsGrid.clientHeight;

      return {
        left: `${offsetLeft + gridWidth / 2 - 91}px`,
        top: `${offsetTop + gridHeight + 10}px`,
      };
    }
    return {};
  };

  const getKeyForPublishParams = (accountId: SocialMediaAccountId): string => {
    return `publish-params-${accountId}`;
  };

  const getAddDetailsLeftOffsetForNavControls = (): string => {
    const publishControls = document.getElementById('publish-options-menu-icon');
    const scheduleControls = document.getElementById('add-details-onboarding-step-three');
    const popupWidth = $('.user-walkthrough-popup:visible').width();

    if (scheduleControls) {
      const scheduleControlsLeftOffset = scheduleControls.offsetLeft;
      const scheduleControlsWidth = scheduleControls.clientWidth;
      return `${scheduleControlsLeftOffset - (popupWidth || 0) + 50 + scheduleControlsWidth / 2}px`;
    }

    if (publishControls) {
      const publishControlsLeftOffset = publishControls.offsetLeft;
      return `${publishControlsLeftOffset - (popupWidth || 0) + 65}px`;
    }

    return '0';
  };

  function findAncestorWithId(element: HTMLElement | null, parentId: string): HTMLElement | null {
    let currentElement = element;

    while (currentElement && currentElement.id !== parentId) {
      currentElement = currentElement.parentElement;
    }

    return currentElement;
  }

  const getResizeOnboarding = (): {} => {
    const resizeButton = document.getElementById('add-details-onboarding-resize-button');
    const popupWidth = $('.user-walkthrough-popup:visible').width() || 0;
    const popupHeight = $('.user-walkthrough-popup:visible').height() || 0;

    if (!resizeButton) {
      return {};
    }

    const ancestorPreviewContainer = findAncestorWithId(resizeButton, 'add-details-onboarding-container-step-one');

    if (!ancestorPreviewContainer) {
      return {};
    }

    const parentDiv = ancestorPreviewContainer.parentElement;

    if (!parentDiv) {
      return {};
    }

    const parentDivLeftOffset = parentDiv.offsetLeft;
    const resizeButtonWidth = resizeButton.clientWidth;
    const resizeButtonHeight = resizeButton.clientHeight;
    const parentDivWidth = parentDiv.clientWidth;
    const parentDivHeight = parentDiv.clientHeight;
    const parentDivTopOffset = parentDiv.offsetTop;
    const offsetResizeButtonFromParentDiv = (parentDivWidth - resizeButtonWidth) / 2 - 20;
    const offsetResizeButtonTopFromParentDiv = parentDivTopOffset + parentDivHeight - resizeButtonHeight - 12;

    const leftOffset = `${parentDivLeftOffset - popupWidth + offsetResizeButtonFromParentDiv}`;
    const topOffset = `${-popupHeight - 8 + 52 + offsetResizeButtonTopFromParentDiv + resizeButtonHeight * 1.5}`;

    return {
      left: `${leftOffset}px`,
      top: `${topOffset}px`,
    };
  };

  const getAddDetailsOnboarding = (stepNumber: number): {} => {
    if (stepNumber === 1) {
      const onboardingStepOneContainer = document.querySelectorAll(`.${PUBLISH_PARAM_HANDLER_CLASS}[data-key="${getKeyForPublishParams(accountIdsToPublishto[0])}"]`)[0];
      const publishingParamsWidth = onboardingStepOneContainer?.clientWidth;
      const publishingParamsHeight = onboardingStepOneContainer?.clientHeight;
      const publishingParamsOffsetTop = (onboardingStepOneContainer as HTMLElement).offsetTop;
      const popupWidth = $('.user-walkthrough-popup:visible').width() ?? 0;

      return {
        left: `${130 + (publishingParamsWidth - popupWidth)}px`,
        top: `${publishingParamsOffsetTop + 0.8 * publishingParamsHeight}px`,
      };
    }

    if (stepNumber === 2) {
      return {top: `${26 + 84}px`, left: `60px`};
    }

    const isResizeOnboarding = shouldShowResizePopup();

    if (stepNumber === 3 && isResizeOnboarding) {
      return getResizeOnboarding();
    }

    if ((stepNumber === 3 && !isResizeOnboarding) || stepNumber === 4) {
      const leftOffset = getAddDetailsLeftOffsetForNavControls();
      return {
        top: `68px`,
        left: `${leftOffset}`,
      };
    }

    return {};
  };

  const getParentContainerStylesByOnboardingType = (onboardingType: SocialMediaWizardOnboardingTypes, stepNumber: number): {} => {
    switch (onboardingType) {
      case SocialMediaWizardOnboardingTypes.DESIGN_SELECTOR:
        return getDesignSelectorOnboarding();
      case SocialMediaWizardOnboardingTypes.SELECT_ACCOUNTS:
        return getSelectAccountsOnboarding(stepNumber);
      case SocialMediaWizardOnboardingTypes.EXISTING_ACCOUNTS:
        return getExistingAccountsOnboarding();
      case SocialMediaWizardOnboardingTypes.ADD_DETAILS:
        return getAddDetailsOnboarding(stepNumber);
      default:
        return {};
    }
  };

  const positionParentContainer = (stepNumber: number): void => {
    const parentContainer = $('.user-walkthrough-popup:visible');
    const onboardingType = getOnboardingType(wizardStep);
    parentContainer.css(getParentContainerStylesByOnboardingType(onboardingType, stepNumber));
  };

  const changePopup = (currentOpenedPopup: JQuery<HTMLElement>, popUpToOpen: JQuery<HTMLElement>): void => {
    setNewHeaderTitleForPopup(popUpToOpen);
    setNewStepNumberForPopup(popUpToOpen);
    closeCurrentPopupAndOpenNewPopup(currentOpenedPopup, popUpToOpen);
    positionParentContainer(getCurrentStepNumberForPopup(popUpToOpen));
    setPopupClickability();
  };

  return <></>;
}
